import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Toolbar,
  Alert,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  FileDownload as ExportIcon,
  Edit as EditIcon,
  Sort as SortIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  LockOpen as UnblockIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';

import DateRangePicker from '../components/DateRangePicker';
import DateUtils from '../utils/dateUtils';
import type { Customer, CustomerFilters, CustomerSortConfig, CustomerSortField } from '../models/Customer';
import customerService from '../services/customerService';
import OrderUtils from '../utils/orderUtils';
import CustomerEditDialog from '../components/CustomerEditDialog';
import CustomerProfileDialog from '../components/CustomerProfileDialog';
import BanPhoneDialog from '../components/BanPhoneDialog';
import banService, { toE164Morocco } from '../services/banService';
import { useAdminScope } from '../contexts/AdminScopeContext';
import { describeActiveScope, resolveScopedStoreIds } from '../utils/adminScopeUtils';

const CustomersPage: React.FC = () => {
  const {
    loading: scopeLoading,
    activeScopeType,
    selectedGroupId,
    selectedStoreId,
    selectedGroup,
    selectedStore,
    stores,
  } = useAdminScope();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<CustomerFilters>({});
  const [hasEmail, setHasEmail] = useState<boolean | undefined>(undefined);
  const [hasPhone, setHasPhone] = useState<boolean | undefined>(undefined);
  const [isActive, setIsActive] = useState<boolean | undefined>(undefined);
  const [minOrders, setMinOrders] = useState<number | ''>('');
  const [maxOrders, setMaxOrders] = useState<number | ''>('');
  const [nextCursor, setNextCursor] = useState<any>(undefined);
  const [fetchingMore, setFetchingMore] = useState(false);

  // Edit dialog
  const [formOpen, setFormOpen] = useState(false);
  const [formCustomer, setFormCustomer] = useState<Customer | undefined>(undefined);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [anonymizeOrders, setAnonymizeOrders] = useState(false);

  // Banned-only filter + totals
  const [showBannedOnly, setShowBannedOnly] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [existingUsersCount, setExistingUsersCount] = useState<number | null>(null);
  const [existingUsersCountLoading, setExistingUsersCountLoading] = useState(false);

  // Ban dialog
  const [banOpen, setBanOpen] = useState(false);
  const [banLoading, setBanLoading] = useState(false);
  const [banCustomer, setBanCustomer] = useState<Customer | null>(null);

  // Profile dialog
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileCustomer, setProfileCustomer] = useState<Customer | null>(null);

  // Banned phone cache for current page (E.164 -> in Set)
  const [bannedPhones, setBannedPhones] = useState<Set<string>>(new Set());

  // Notifications
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const sortFields: CustomerSortField[] = [
    'createdAt', 'name', 'phone', 'email', 'totalOrders', 'totalSpent', 'lastOrderDate', 'isActive'
  ];
  const [sortConfig, setSortConfig] = useState<CustomerSortConfig>({
    field: 'createdAt',
    direction: 'desc'
  });
  const scopedStoreIds = useMemo(
    () =>
      resolveScopedStoreIds({
        activeScopeType,
        selectedGroupId,
        selectedStoreId,
        stores,
      }),
    [activeScopeType, selectedGroupId, selectedStoreId, stores]
  );
  const isScopedMode = scopedStoreIds !== null;
  const scopeSummary = useMemo(
    () =>
      describeActiveScope({
        activeScopeType,
        selectedGroupId,
        selectedStoreId,
        stores,
      }),
    [activeScopeType, selectedGroupId, selectedStoreId, stores]
  );
  const aggregateScope = useMemo(() => {
    if (selectedStoreId) {
      return { scopeType: 'store' as const, scopeId: selectedStoreId };
    }
    if (selectedGroupId) {
      return { scopeType: 'group' as const, scopeId: selectedGroupId };
    }
    return null;
  }, [selectedGroupId, selectedStoreId]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const buildFilters = (): CustomerFilters => {
    return {
      dateRange: filters.dateRange,
      searchTerm: searchTerm || undefined,
      bannedOnly: showBannedOnly,
      hasEmail,
      hasPhone,
      isActive,
      minOrders: typeof minOrders === 'number' ? minOrders : undefined,
      maxOrders: typeof maxOrders === 'number' ? maxOrders : undefined
    };
  };

  const loadCustomers = async (append = false) => {
    try {
      if (!append) {
        setLoading(true);
      } else {
        setFetchingMore(true);
      }

      if (isScopedMode) {
        if (!scopedStoreIds || scopedStoreIds.length === 0) {
          setCustomers([]);
          setNextCursor(undefined);
          return;
        }

        if (!aggregateScope) {
          setCustomers([]);
          setNextCursor(undefined);
          return;
        }

        const scopedFilters: CustomerFilters = {
          dateRange: filters.dateRange,
          searchTerm: searchTerm || undefined,
          bannedOnly: showBannedOnly,
          hasEmail,
          hasPhone,
          isActive,
          minOrders: typeof minOrders === 'number' ? minOrders : undefined,
          maxOrders: typeof maxOrders === 'number' ? maxOrders : undefined,
        };
        const res = await customerService.listScopedCustomers(
          aggregateScope,
          scopedFilters,
          100,
          append ? nextCursor : undefined
        );
        if (append) {
          setCustomers(prev => [...prev, ...res.customers]);
        } else {
          setCustomers(res.customers);
        }
        setNextCursor(res.nextCursor);
        return;
      }

      const trimmed = searchTerm.trim();
      const digits = trimmed.replace(/[^\d]/g, '');
      const isPhoneLike = !!trimmed && /^[+\d][\d+\s]*$/.test(trimmed) && digits.length >= 3;
      const isEmailLike = !!trimmed && trimmed.includes('@');

      const baseFilters: CustomerFilters = {
        dateRange: filters.dateRange,
        bannedOnly: showBannedOnly,
        hasEmail,
        hasPhone,
        isActive,
        minOrders: typeof minOrders === 'number' ? minOrders : undefined,
        maxOrders: typeof maxOrders === 'number' ? maxOrders : undefined,
      };

      // Cas 1 : téléphone ou email -> on laisse le backend filtrer comme avant
      if (isPhoneLike || isEmailLike) {
        const res = await customerService.listCustomers(
          { ...baseFilters, searchTerm: trimmed },
          30,
          append ? nextCursor : undefined
        );
        if (append) {
          setCustomers(prev => [...prev, ...res.customers]);
        } else {
          setCustomers(res.customers);
        }
        setNextCursor(res.nextCursor);
        return;
      }

      // Cas 2 : aucun terme de recherche -> pagination classique, pas de scan complet
      if (!trimmed) {
        const res = await customerService.listCustomers(
          baseFilters,
          30,
          append ? nextCursor : undefined
        );
        if (append) {
          setCustomers(prev => [...prev, ...res.customers]);
        } else {
          setCustomers(res.customers);
        }
        setNextCursor(res.nextCursor);
        return;
      }

      // Cas 3 : recherche par nom/texte -> on applique la même logique que dans CustomerPhoneSelector
      let all: Customer[] = [];
      let cursorLocal: any | undefined = undefined;

      for (;;) {
        const res = await customerService.listCustomers(
          baseFilters,
          200,
          cursorLocal
        );
        all = all.concat(res.customers);
        if (!res.nextCursor) {
          break;
        }
        cursorLocal = res.nextCursor;
      }

      const term = trimmed.toLowerCase();

      const filtered = all.filter((c) => {
        const first = (c.firstName || '').toLowerCase();
        const last = (c.lastName || '').toLowerCase();
        const phone = (c.phone || '').toLowerCase();
        const email = (c.email || '').toLowerCase();
        // Noms : préfixe seulement (Ali -> Ali, Alia, ALI..., pas Bakali)
        const nameMatch = first.startsWith(term) || last.startsWith(term);
        // Téléphone/email : helper, on permet aussi la recherche là-dessus
        const contactMatch = phone.includes(term) || email.startsWith(term);
        return nameMatch || contactMatch;
      });

      setCustomers(filtered);
      setNextCursor(undefined);
    } catch (error) {
      console.error('Erreur chargement clients:', error);
      showSnackbar('Erreur lors du chargement des clients', 'error');
    } finally {
      setLoading(false);
      setFetchingMore(false);
    }
  };

  useEffect(() => {
    if (scopeLoading) {
      return;
    }
    loadCustomers(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeLoading, showBannedOnly, filters.dateRange, activeScopeType, selectedGroupId, selectedStoreId]);

  // Re-apply local filters and sorting whenever dependencies change
  useEffect(() => {
    let list = [...customers];

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      list = list.filter((customer) => {
        const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim().toLowerCase();
        const phone = (customer.phone || '').toLowerCase();
        const email = (customer.email || '').toLowerCase();
        return fullName.includes(term) || phone.includes(term) || email.includes(term);
      });
    }

    // Extra filters toggles (for real-time UX before re-fetch)
    if (hasEmail !== undefined) {
      list = list.filter(c => hasEmail ? !!(c.email && c.email.trim()) : !(c.email && c.email.trim()));
    }
    if (hasPhone !== undefined) {
      list = list.filter(c => hasPhone ? !!(c.phone && c.phone.trim()) : !(c.phone && c.phone.trim()));
    }
    if (isActive !== undefined) {
      list = list.filter(c => (c.stats?.isActive ?? false) === isActive);
    }
    if (typeof minOrders === 'number') {
      list = list.filter(c => (c.stats?.totalOrders ?? 0) >= minOrders);
    }
    if (typeof maxOrders === 'number') {
      list = list.filter(c => (c.stats?.totalOrders ?? 0) <= maxOrders);
    }

    // Sorting
    list.sort((a, b) => {
      let aValue: any = '';
      let bValue: any = '';

      switch (sortConfig.field) {
        case 'name':
          aValue = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
          bValue = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
          break;
        case 'phone':
          aValue = (a.phone || '').toLowerCase();
          bValue = (b.phone || '').toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'createdAt':
          aValue = a.createdAt ? a.createdAt.getTime() : 0;
          bValue = b.createdAt ? b.createdAt.getTime() : 0;
          break;
        case 'totalOrders':
          aValue = a.stats?.totalOrders ?? 0;
          bValue = b.stats?.totalOrders ?? 0;
          break;
        case 'totalSpent':
          aValue = a.stats?.totalSpent ?? 0;
          bValue = b.stats?.totalSpent ?? 0;
          break;
        case 'lastOrderDate':
          aValue = a.stats?.lastOrderDate ? a.stats.lastOrderDate.getTime() : 0;
          bValue = b.stats?.lastOrderDate ? b.stats.lastOrderDate.getTime() : 0;
          break;
        case 'isActive':
          aValue = a.stats?.isActive ? 1 : 0;
          bValue = b.stats?.isActive ? 1 : 0;
          break;
        default:
          break;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredCustomers(list);
  }, [customers, searchTerm, hasEmail, hasPhone, isActive, minOrders, maxOrders, sortConfig, showBannedOnly, bannedPhones]);

  // Load banned status for current page phones
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const uniquePhones = Array.from(
          new Set(
            customers
              .map((c) => toE164Morocco(c.phone))
              .filter((p): p is string => !!p)
          )
        );
        if (uniquePhones.length === 0) {
          if (!cancelled) setBannedPhones(new Set());
          return;
        }
        if (!cancelled) {
          setBannedPhones(await banService.getBannedPhones(uniquePhones));
        }
      } catch (e) {
        // ignore – UI can still work without badge
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customers]);

  // Count total customers (server aggregation) for basic awareness (date range + phone search supported)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (scopeLoading) {
        if (!cancelled) {
          setCountLoading(false);
          setExistingUsersCountLoading(false);
        }
        return;
      }
      try {
        setCountLoading(true);
        if (isScopedMode) {
          setExistingUsersCountLoading(true);
        }
        const countFilters: CustomerFilters = {
          dateRange: filters.dateRange,
          searchTerm: searchTerm || undefined,
          bannedOnly: showBannedOnly,
        };
        if (isScopedMode && aggregateScope) {
          const [scopeRes, usersRes] = await Promise.all([
            customerService.countScopedCustomers(aggregateScope, countFilters),
            customerService.countCustomers(countFilters),
          ]);
          if (!cancelled) {
            setTotalCount(scopeRes.count);
            setExistingUsersCount(usersRes.count);
          }
        } else {
          const res = await customerService.countCustomers(countFilters);
          if (!cancelled) {
            setTotalCount(res.count);
            setExistingUsersCount(null);
          }
        }
      } catch {
        if (!cancelled) {
          setTotalCount(null);
          setExistingUsersCount(null);
        }
      } finally {
        if (!cancelled) {
          setCountLoading(false);
          setExistingUsersCountLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-count when date range or search term changes
  }, [scopeLoading, filters.dateRange, searchTerm, showBannedOnly, isScopedMode, aggregateScope]);

  const isAllSelected = selected.size === filteredCustomers.length && filteredCustomers.length > 0;
  const isIndeterminate = selected.size > 0 && selected.size < filteredCustomers.length;

  const handleSelectRow = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredCustomers.map(c => c.id)));
    }
  };

  const handleSort = (field: CustomerSortField) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const refresh = async () => {
    setSelected(new Set());
    await loadCustomers(false);
  };

  // Delete
  const openDelete = () => {
    if (selected.size === 0) return;
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      const ids = Array.from(selected);
      const res = await customerService.deleteCustomers(ids, { anonymizeOrders });
      if (res.success) {
        showSnackbar(`${res.deletedCount} client(s) supprimé(s)`, 'success');
        setSelected(new Set());
        setDeleteOpen(false);
        await loadCustomers(false);
      } else {
        showSnackbar(`Suppression partielle: ${res.deletedCount} supprimé(s)`, 'warning');
      }
    } catch (error) {
      console.error('Erreur suppression clients:', error);
      showSnackbar('Erreur lors de la suppression des clients', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Edit
  const openEdit = (customer: Customer) => {
    setFormCustomer(customer);
    setFormOpen(true);
    setFormError(null);
  };

  const closeForm = () => {
    setFormOpen(false);
    setFormCustomer(undefined);
    setFormError(null);
  };

  const submitForm = async (data: { firstName: string; lastName: string; phone: string; email?: string }) => {
    if (!formCustomer) return;
    try {
      setFormLoading(true);
      await customerService.updateCustomer(formCustomer.id, data);
      closeForm();
      await loadCustomers(false);
      showSnackbar('Client mis à jour', 'success');
    } catch (error: any) {
      setFormError(error?.message || 'Erreur mise à jour client');
    } finally {
      setFormLoading(false);
    }
  };

  // Ban/Unban handlers
  const openProfile = (customer: Customer) => {
    setProfileCustomer(customer);
    setProfileOpen(true);
  };

  const openBan = (customer: Customer) => {
    setBanCustomer(customer);
    setBanOpen(true);
  };

  const confirmBan = async (reason: string) => {
    if (!banCustomer) return;
    const e164 = toE164Morocco(banCustomer.phone);
    if (!e164) {
      showSnackbar('Numéro invalide pour bannissement', 'error');
      return;
    }
    try {
      setBanLoading(true);
      await banService.banPhone(e164, reason);
      setBannedPhones((prev) => {
        const next = new Set(prev);
        next.add(e164);
        return next;
      });
      showSnackbar('Numéro banni', 'success');
      setBanOpen(false);
      setBanCustomer(null);
    } catch (e) {
      showSnackbar('Erreur lors du bannissement', 'error');
    } finally {
      setBanLoading(false);
    }
  };

  const handleUnban = async (customer: Customer) => {
    const e164 = toE164Morocco(customer.phone);
    if (!e164) {
      showSnackbar('Numéro invalide', 'error');
      return;
    }
    try {
      await banService.unbanPhone(e164);
      setBannedPhones((prev) => {
        const next = new Set(prev);
        next.delete(e164);
        return next;
      });
      showSnackbar('Numéro débanni', 'success');
    } catch (e) {
      showSnackbar('Erreur lors du débannissement', 'error');
    }
  };

  // Export CSV
  const exportCSV = () => {
    const headers = [
      'ID',
      'Nom',
      'Téléphone',
      'Email',
      'Date de Création',
      'Nombre de Commandes',
      'Total Dépensé',
      'Dernière Commande',
      'Actif'
    ];

    const rows = filteredCustomers.map(c => {
      const fullName = `${c.firstName || ''} ${c.lastName || ''}`.trim();
      return [
        c.id,
        fullName,
        c.phone || '',
        c.email || '',
        c.createdAt ? c.createdAt.toLocaleDateString('fr-FR') : '',
        String(c.stats?.totalOrders ?? 0),
        OrderUtils.formatAmount(c.stats?.totalSpent ?? 0),
        c.stats?.lastOrderDate ? c.stats.lastOrderDate.toLocaleDateString('fr-FR') : '',
        (c.stats?.isActive ? 'Oui' : 'Non')
      ];
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', `customers_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);

    showSnackbar(`${rows.length} client(s) exporté(s)`, 'success');
  };

  // Derived stats
  const totalDisplayed = filteredCustomers.length;
  const totalActive = filteredCustomers.filter(c => c.stats?.isActive).length;
  const totalRevenue = filteredCustomers.reduce((sum, c) => sum + (c.stats?.totalSpent ?? 0), 0);
  const avgOrders = filteredCustomers.length > 0
    ? filteredCustomers.reduce((sum, c) => sum + (c.stats?.totalOrders ?? 0), 0) / filteredCustomers.length
    : 0;

  const customersForDeletePreview = useMemo(() => {
    return filteredCustomers.filter(c => selected.has(c.id));
  }, [filteredCustomers, selected]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PeopleIcon color="primary" />
        Gestion des Clients
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Scope actif: {scopeSummary}
        {selectedGroup ? ` · Groupe ${selectedGroup.name}` : ''}
        {selectedStore ? ` · Magasin ${selectedStore.name}` : ''}
      </Alert>

      {/* Filtres */}
      <DateRangePicker
        value={filters.dateRange}
        onChange={(dr) => setFilters(prev => ({ ...prev, dateRange: dr || undefined }))}
        label='Filtrer par période (date de création client)'
      />

      <Paper sx={{ mb: 2 }}>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Rechercher (nom, téléphone, email)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { loadCustomers(false); } }}
            InputProps={{
              startAdornment: (
                <SearchIcon
                  sx={{ mr: 1, color: 'text.secondary', cursor: 'pointer' }}
                  onClick={() => loadCustomers(false)}
                  titleAccess="Rechercher"
                />
              )
            }}
            sx={{ minWidth: 300 }}
          />

          <TextField
            size="small"
            type="number"
            label="Min commandes"
            value={minOrders}
            onChange={(e) => setMinOrders(e.target.value === '' ? '' : Number(e.target.value))}
            sx={{ width: 150 }}
          />
          <TextField
            size="small"
            type="number"
            label="Max commandes"
            value={maxOrders}
            onChange={(e) => setMaxOrders(e.target.value === '' ? '' : Number(e.target.value))}
            sx={{ width: 150 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={hasEmail === true}
                indeterminate={hasEmail === undefined}
                onChange={() => setHasEmail(prev => prev === true ? false : prev === false ? undefined : true)}
              />
            }
            label={hasEmail === undefined ? 'Email (tous)' : hasEmail ? 'Avec Email' : 'Sans Email'}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={hasPhone === true}
                indeterminate={hasPhone === undefined}
                onChange={() => setHasPhone(prev => prev === true ? false : prev === false ? undefined : true)}
              />
            }
            label={hasPhone === undefined ? 'Téléphone (tous)' : hasPhone ? 'Avec Téléphone' : 'Sans Téléphone'}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isActive === true}
                indeterminate={isActive === undefined}
                onChange={() => setIsActive(prev => prev === true ? false : prev === false ? undefined : true)}
              />
            }
            label={isActive === undefined ? 'Actif (tous)' : isActive ? 'Actif' : 'Inactif'}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={showBannedOnly}
                onChange={(e) => setShowBannedOnly(e.target.checked)}
              />
            }
            label="Bannis uniquement"
          />

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadCustomers(false)}
          >
            Appliquer/F5
          </Button>

          {selected.size > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={openDelete}
              disabled={deleting}
            >
              Supprimer ({selected.size})
            </Button>
          )}

          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={exportCSV}
          >
            Exporter
          </Button>
        </Toolbar>
      </Paper>

      {/* Statistiques rapides */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" color="primary">
              {totalDisplayed}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Clients affichés (liste filtrée)
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" color="secondary.main">
              {countLoading ? '...' : (totalCount ?? '—')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isScopedMode ? 'Clients acheteurs uniques (scope)' : 'Base clients globale (users)'}
            </Typography>
          </Box>
          {isScopedMode && (
            <Box>
              <Typography variant="h4" color="warning.main">
                {existingUsersCountLoading ? '...' : (existingUsersCount ?? '—')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Clients existants (users global)
              </Typography>
            </Box>
          )}
          <Box>
            <Typography variant="h4" color="success.main">
              {OrderUtils.formatAmount(totalRevenue)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total dépensé (affiché)
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" color="info.main">
              {avgOrders.toFixed(1)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Commandes moyennes / client
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" color="secondary.main">
              {totalActive}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Clients actifs
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Tableau des clients */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>
                <Button size="small" onClick={() => handleSort('name')} endIcon={<SortIcon />}>
                  Nom
                </Button>
              </TableCell>
              <TableCell>
                <Button size="small" onClick={() => handleSort('phone')} endIcon={<SortIcon />}>
                  Téléphone
                </Button>
              </TableCell>
              <TableCell>
                <Button size="small" onClick={() => handleSort('email')} endIcon={<SortIcon />}>
                  Email
                </Button>
              </TableCell>
              <TableCell>
                <Button size="small" onClick={() => handleSort('totalOrders')} endIcon={<SortIcon />}>
                  Nb Commandes
                </Button>
              </TableCell>
              <TableCell>
                <Button size="small" onClick={() => handleSort('totalSpent')} endIcon={<SortIcon />}>
                  Total Dépensé
                </Button>
              </TableCell>
              <TableCell>
                <Button size="small" onClick={() => handleSort('lastOrderDate')} endIcon={<SortIcon />}>
                  Dernière Commande
                </Button>
              </TableCell>
              <TableCell>
                <Button size="small" onClick={() => handleSort('isActive')} endIcon={<SortIcon />}>
                  Actif
                </Button>
              </TableCell>
              <TableCell>
                <Button size="small" onClick={() => handleSort('createdAt')} endIcon={<SortIcon />}>
                  Créé le
                </Button>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredCustomers.map((c) => {
              const fullName = `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Client';
              return (
                <TableRow key={c.id} hover onClick={() => openProfile(c)} sx={{ cursor: 'pointer' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(c.id)}
                      onChange={() => handleSelectRow(c.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {fullName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">{c.phone || 'N/A'}</Typography>
                      {c.isBanned ? <Chip label="Banni" size="small" color="error" /> : null}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{c.email || 'N/A'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{c.stats?.totalOrders ?? 0}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {OrderUtils.formatAmount(c.stats?.totalSpent ?? 0)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {c.stats?.lastOrderDate ? DateUtils.formatDateFR(c.stats.lastOrderDate) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={c.stats?.isActive ? 'Actif' : 'Inactif'}
                      size="small"
                      color={c.stats?.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {c.createdAt ? DateUtils.formatDateFR(c.createdAt) : '—'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" title="Voir profil" onClick={(e) => { e.stopPropagation(); openProfile(c); }} sx={{ mr: 0.5 }}>
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton size="small" title="Modifier" onClick={(e) => { e.stopPropagation(); openEdit(c); }}>
                      <EditIcon />
                    </IconButton>
                    {(() => {
                      const phoneE164 = toE164Morocco(c.phone);
                      return c.isBanned ? (
                        <IconButton
                          size="small"
                          color="success"
                          title="Débannir"
                          onClick={(e) => { e.stopPropagation(); handleUnban(c); }}
                          disabled={!phoneE164}
                          sx={{ ml: 0.5 }}
                        >
                          <UnblockIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          color="error"
                          title="Bannir"
                          onClick={(e) => { e.stopPropagation(); openBan(c); }}
                          disabled={!phoneE164}
                          sx={{ ml: 0.5 }}
                        >
                          <BlockIcon />
                        </IconButton>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {(nextCursor || fetchingMore) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Button
            variant="outlined"
            onClick={() => loadCustomers(true)}
            disabled={fetchingMore}
          >
            {fetchingMore ? 'Chargement...' : 'Charger plus'}
          </Button>
        </Box>
      )}

      {filteredCustomers.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Aucun client trouvé
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Essayez de modifier vos filtres de recherche
          </Typography>
        </Box>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Dialog suppression clients */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Vous êtes sur le point de supprimer {selected.size} client(s). Cette action supprimera également leur autorisation (compte Auth/numéro).
          </Alert>
          <Box sx={{ maxHeight: 240, overflow: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
            {customersForDeletePreview.map(c => {
              const fullName = `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Client';
              return (
                <Box key={c.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="body2">{fullName} — {c.phone || 'N/A'} — {c.email || 'N/A'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {c.stats?.totalOrders ?? 0} cmd / {OrderUtils.formatAmount(c.stats?.totalSpent ?? 0)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={anonymizeOrders}
                onChange={(e) => setAnonymizeOrders(e.target.checked)}
              />
            }
            label="Anonymiser aussi l'historique de commandes (recommandé pour RGPD)"
            sx={{ mt: 2 }}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            Par défaut, les commandes sont conservées (pour analytics/comptabilité) sans données personnelles affichées.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Annuler</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Suppression...' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'édition */}
      <CustomerEditDialog
        open={formOpen}
        onClose={closeForm}
        customer={formCustomer ?? null}
        onSaved={async () => { await loadCustomers(false); showSnackbar('Client mis à jour', 'success'); }}
      />

      {/* Dialog bannissement */}
      <BanPhoneDialog
        open={banOpen}
        onClose={() => { if (!banLoading) { setBanOpen(false); setBanCustomer(null); } }}
        onConfirm={confirmBan}
        loading={banLoading}
        customerName={`${banCustomer?.firstName || ''} ${banCustomer?.lastName || ''}`.trim()}
        phone={banCustomer?.phone}
      />

      {/* Dialog profil client */}
      <CustomerProfileDialog
        open={profileOpen}
        customer={profileCustomer}
        onClose={() => { setProfileOpen(false); setProfileCustomer(null); }}
        onSaved={() => loadCustomers(false)}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomersPage;
